import React, { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, AlertCircle, RefreshCw, Activity } from "lucide-react";
import { Button } from "./components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function timeAgo(iso) {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export function PixelStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/tracking/pixel-status`);
      setStatus(data);
    } catch (err) {
      console.warn("pixel-status fetch failed (banner stays unverified):", err?.message || err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30_000); // re-poll every 30s
    return () => clearInterval(t);
  }, []);

  const installed = !!status?.installed;
  const recent = (status?.clicks_24h || 0) > 0 || (status?.sales_24h || 0) > 0;

  const tone = recent
    ? { ring: "border-emerald-500/30", glow: "shadow-[0_0_18px_-8px_rgba(16,185,129,0.5)]", accent: "text-emerald-400", icon: CheckCircle2, status: "Active", desc: "Pixel is firing — events received in the last 24 hours." }
    : installed
      ? { ring: "border-amber-500/30", glow: "shadow-[0_0_18px_-8px_rgba(245,158,11,0.5)]", accent: "text-amber-400", icon: CheckCircle2, status: "Installed but quiet", desc: `No new events in the last 24h. Last click ${timeAgo(status.last_click_ts)}.` }
      : { ring: "border-red-500/30", glow: "shadow-[0_0_18px_-8px_rgba(239,68,68,0.5)]", accent: "text-red-400", icon: AlertCircle, status: "Not detected yet", desc: "We haven't seen any traffic from your pixel. Paste the snippet on your site and visit a page with the UTM link." };

  const Icon = tone.icon;

  return (
    <section
      className={`relative overflow-hidden rounded-2xl border ${tone.ring} ${tone.glow} bg-white/[0.02] backdrop-blur-xl p-5 sm:p-6 animate-fade-up`}
      data-testid="pixel-status-panel"
    >
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center ${tone.accent}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`text-base font-semibold ${tone.accent}`} data-testid="pixel-status-label">{tone.status}</span>
            {installed && (
              <span className="text-xs text-zinc-500">
                {status.clicks_24h} clicks · {status.sales_24h} sales · last 24h
              </span>
            )}
          </div>
          <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{tone.desc}</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={refresh}
          disabled={loading}
          className="text-zinc-400 hover:text-white hover:bg-white/5 flex-shrink-0"
          data-testid="pixel-status-refresh"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {installed && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 pt-5 border-t border-white/5 text-sm">
          <Metric label="Last click" value={timeAgo(status.last_click_ts)} icon={Activity} />
          <Metric label="Last sale" value={timeAgo(status.last_sale_ts)} icon={Activity} />
          <Metric label="Clicks · 24h" value={status.clicks_24h} icon={Activity} />
          <Metric label="Sales · 24h" value={status.sales_24h} icon={Activity} />
        </div>
      )}
    </section>
  );
}

function Metric({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-1 flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-white font-medium tabular-nums">{value}</div>
    </div>
  );
}
